/**
 * "Sync-Status (letzte Läufe)" — the most recent sync runs.
 *
 * Order is the server's (`started_at DESC`). The trailing figure is
 * `fetched_count`, the only one of the run's three counters that answers "how
 * much did this run see" — `upserted_count` and `unchanged_count` are a
 * breakdown of it, and picking one of those would silently under-report.
 *
 * The design reference also shows a duration, a trigger source, retries and an
 * API-call count. `bexio_sync_runs` stores none of those, so none is shown.
 *
 * The footer links to `/admin/bexio/sync-runs`, the full history with its own
 * filters and per-run detail. This card stays a preview — the same five rows,
 * the same three facts — and only gained the way out once that page existed.
 */
import React from "react";
import { AlertTriangle, CheckCircle2, CircleAlert, Loader2 } from "lucide-react";

import { formatDateTime } from "../../../../utils/datetime";
import { SCOPE_LABEL, SYNC_STATUS_LABEL, mapLabel, syncStatusTone } from "../bexioFormat";
import BexioPreviewFooter from "./BexioPreviewFooter";

/** The routed history page this card previews. */
export const SYNC_RUNS_ROUTE = "/admin/bexio/sync-runs";

/** Icon per status, so the state is never carried by colour alone. */
const ICON = {
  good: { Comp: CheckCircle2, cls: "text-emerald-600 dark:text-emerald-400" },
  bad: { Comp: CircleAlert, cls: "text-red-600 dark:text-red-400" },
  warn: { Comp: Loader2, cls: "text-amber-600 dark:text-amber-400" },
  neutral: { Comp: AlertTriangle, cls: "text-muted-foreground" },
};

const PILL = {
  good:
    "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400",
  bad:
    "border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300",
  warn:
    "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
  neutral: "border-border bg-muted text-muted-foreground",
};

export default function BexioSyncPreview({ runs, total, onLoadMore, loadingMore }) {
  const rows = Array.isArray(runs) ? runs : [];

  return (
    <section
      aria-labelledby="bexio-sync-preview-title"
      className="flex flex-col rounded-xl border border-border bg-card"
    >
      <div className="border-b border-border px-4 py-3">
        <h2
          id="bexio-sync-preview-title"
          className="text-sm font-semibold text-foreground"
        >
          Sync-Status (letzte Läufe)
        </h2>
      </div>

      {rows.length === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">
          Noch keine Sync-Läufe.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((r) => {
            const tone = syncStatusTone(r.status);
            const { Comp, cls } = ICON[tone] || ICON.neutral;
            return (
              <li key={r.id} className="flex items-center gap-3 px-4 py-2">
                <Comp aria-hidden="true" className={`h-4 w-4 shrink-0 ${cls}`} />
                <span className="w-24 shrink-0 truncate text-sm text-foreground">
                  {mapLabel(SCOPE_LABEL, r.entity_scope)}
                </span>
                <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                  {formatDateTime(r.started_at)}
                </span>
                <span
                  className={`inline-flex shrink-0 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium ${
                    PILL[tone] || PILL.neutral
                  }`}
                >
                  {mapLabel(SYNC_STATUS_LABEL, r.status)}
                </span>
                <span className="w-10 shrink-0 text-right text-sm tabular-nums text-muted-foreground">
                  {r.fetched_count ?? "—"}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <BexioPreviewFooter
        shown={rows.length}
        total={total}
        noun="Lauf"
        nounPlural="Läufe"
        onLoadMore={onLoadMore}
        loadingMore={loadingMore}
        linkTo={SYNC_RUNS_ROUTE}
        linkLabel="Alle Sync-Läufe anzeigen"
      />
    </section>
  );
}
